import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Segmented,
  Select,
  Space,
  Statistic,
  Switch,
  Tag,
  Typography
} from "antd";
import { SendOutlined, SettingOutlined, StarOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import PageToolbar from "../../../design-system/PageToolbar";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import {
  getReviewMeta,
  getReviews,
  saveReviewSettings,
  sendReviewRequest
} from "../notifications/api";
import {
  STATUS_COLORS,
  channelLabels,
  describeReason,
  formatDateTime,
  statusLabels
} from "../notifications/labels";
import "../notifications/notifications.css";

const PAGE_SIZE = 25;

const triggerLabels = t => ({
  invoice_paid: t("Invoice paid"),
  project_completed: t("Project completed"),
  manual: t("Sent by hand")
});

const SettingsModal = ({ open, settings, onClose, onSaved }) => {
  const { t } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const placeId = Form.useWatch("googlePlaceId", form);

  useEffect(() => {
    if (open && settings) form.setFieldsValue(settings);
  }, [open, settings]);

  const save = async values => {
    setBusy(true);
    try {
      const res = await saveReviewSettings(values);
      Success(t("Settings saved"));
      onSaved(res.data.settings);
      onClose();
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      rootClassName="notifications-page"
      open={open}
      width={720}
      title={t("Review settings")}
      okText={t("Save")}
      cancelText={t("Cancel")}
      confirmLoading={busy}
      onOk={() => form.submit()}
      onCancel={onClose}
    >
      <Form form={form} layout="vertical" onFinish={save}>
        <Form.Item
          name="enabled"
          valuePropName="checked"
          label={t("Ask customers for a review automatically")}
          extra={t("Only work finished after switching this on triggers a request.")}
        >
          <Switch />
        </Form.Item>
        <Form.Item
          name="googlePlaceId"
          label={t("Google Place ID")}
          extra={
            <span>
              {t("Find it with Google's Place ID Finder; it starts with ChIJ.")}{" "}
              <a
                href="https://developers.google.com/maps/documentation/places/web-service/place-id"
                target="_blank"
                rel="noreferrer"
              >
                {t("Place ID Finder")}
              </a>
            </span>
          }
        >
          <Input maxLength={200} placeholder="ChIJ…" />
        </Form.Item>
        {placeId && (
          <Typography.Paragraph className="nt-small nt-wrap">
            {t("Review link")}:{" "}
            <a
              href={`https://search.google.com/local/writereview?placeid=${encodeURIComponent(placeId)}`}
              target="_blank"
              rel="noreferrer"
            >
              {`https://search.google.com/local/writereview?placeid=${placeId}`}
            </a>
          </Typography.Paragraph>
        )}
        <Row gutter={[16, 0]}>
          <Col xs={24} md={12}>
            <Form.Item name="provenExpertUrl" label={t("ProvenExpert link (optional)")}>
              <Input maxLength={500} placeholder="https://www.provenexpert.com/…" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="myHammerUrl" label={t("MyHammer link (optional)")}>
              <Input maxLength={500} placeholder="https://www.my-hammer.de/…" />
            </Form.Item>
          </Col>
        </Row>
        <Typography.Title level={5}>{t("When to ask")}</Typography.Title>
        <Row gutter={[16, 0]}>
          <Col xs={24} md={12}>
            <Form.Item name="onInvoicePaid" valuePropName="checked">
              <Checkbox>{t("When an invoice is paid in full")}</Checkbox>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="onProjectCompleted" valuePropName="checked">
              <Checkbox>{t("When a project is marked completed")}</Checkbox>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="delayDays" label={t("Wait before asking")}>
              <InputNumber
                className="nt-full"
                min={0}
                max={60}
                precision={0}
                addonAfter={t("days")}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="customerCooldownDays" label={t("Ask the same customer at most every")}>
              <InputNumber
                className="nt-full"
                min={0}
                max={3650}
                precision={0}
                addonAfter={t("days")}
              />
            </Form.Item>
          </Col>
        </Row>
        <Alert
          type="info"
          showIcon
          message={t(
            "Review requests count as advertising: they only go to customers who agreed to them (Settings → Customer messages → Customer consent). Every request contains a link to unsubscribe."
          )}
        />
      </Form>
    </Modal>
  );
};

const SendModal = ({ open, onClose, onSent }) => {
  const { t } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState(false);
  const client = Form.useWatch("client", form);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setRecent(false);
    getReviewMeta()
      .then(res => setMeta(res.data))
      .catch(generalErrorAlert);
  }, [open]);

  const chosen = meta?.clients.find(c => c._id === client);
  const projects = (meta?.projects || []).filter(p => p.client === client);

  const send = async values => {
    setBusy(true);
    try {
      const res = await sendReviewRequest({ ...values, force: recent });
      const request = res.data.data;
      if (request.status === "sent") Success(t("Review request sent"));
      else Modal.warning({ title: t("Not sent"), content: describeReason(request.reason, t) });
      onSent();
      onClose();
    } catch (error) {
      if (error.response?.data?.code === "RECENTLY_ASKED") setRecent(true);
      else generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      rootClassName="notifications-page"
      open={open}
      title={t("Ask for a review")}
      okText={recent ? t("Send anyway") : t("Send")}
      cancelText={t("Cancel")}
      confirmLoading={busy}
      okButtonProps={{ disabled: !chosen?.reviewsConsent }}
      onOk={() => form.submit()}
      onCancel={onClose}
    >
      <Form form={form} layout="vertical" onFinish={send}>
        <Form.Item
          name="client"
          label={t("Customer")}
          rules={[{ required: true, message: t("Please choose a customer.") }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            loading={!meta}
            onChange={() => {
              form.setFieldsValue({ project: undefined });
              setRecent(false);
            }}
            options={(meta?.clients || []).map(c => ({
              value: c._id,
              label: c.reviewsConsent ? c.name : `${c.name} (${t("no consent")})`
            }))}
          />
        </Form.Item>
        {chosen && !chosen.reviewsConsent && (
          <Alert
            className="nt-gap"
            type="warning"
            showIcon
            message={t("This customer has not agreed to review requests.")}
            action={
              <Link to="/user/settings/messaging">
                <Button size="small">{t("Record consent")}</Button>
              </Link>
            }
          />
        )}
        <Form.Item
          name="project"
          label={t("Project (optional)")}
          extra={t("At most one request per project.")}
        >
          <Select
            allowClear
            disabled={!client}
            options={projects.map(p => ({ value: p._id, label: p.name || "—" }))}
          />
        </Form.Item>
        {recent && (
          <Alert
            type="warning"
            showIcon
            message={t("This customer was asked for a review recently. Send anyway?")}
          />
        )}
      </Form>
    </Modal>
  );
};

const Reviews = () => {
  const { t, i18n } = useTranslation("dashboard");
  const statuses = statusLabels(t);
  const channels = channelLabels(t);
  const triggers = triggerLabels(t);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page: page - 1, pageSize: PAGE_SIZE };
      if (filter === "clicked") params.clicked = "true";
      else if (filter !== "all") params.status = filter;
      const res = await getReviews(params);
      setData(res.data);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [filter, page]);

  const stats = data?.stats || { total: 0, sent: 0, clicked: 0, optedOut: 0 };
  const settings = data?.settings;
  const clickRate = stats.sent ? Math.round((stats.clicked / stats.sent) * 100) : 0;

  const columns = [
    {
      title: t("Customer"),
      key: "client",
      mobile: "title",
      render: (_, row) => (
        <div>
          <Typography.Text strong>{row.clientName || "—"}</Typography.Text>
          <div className="nt-small">
            {[row.projectName, row.invoiceNumber, triggers[row.trigger]]
              .filter(Boolean)
              .join(" · ")}
          </div>
        </div>
      )
    },
    {
      title: t("Status"),
      key: "status",
      mobile: "badge",
      width: 130,
      render: (_, row) => (
        <Space size={[4, 4]} wrap>
          <Tag color={STATUS_COLORS[row.status]}>{statuses[row.status]}</Tag>
          {row.clicks > 0 && <Tag color="gold">{t("Clicked")}</Tag>}
          {row.optedOut && <Tag>{t("Unsubscribed")}</Tag>}
        </Space>
      )
    },
    {
      title: t("Channel"),
      key: "channel",
      width: 110,
      render: (_, row) =>
        row.status === "sent" ? (
          channels[row.channel] || row.channel
        ) : (
          <span className="nt-small">{describeReason(row.reason, t)}</span>
        )
    },
    {
      title: t("Sent"),
      key: "sentAt",
      width: 150,
      render: (_, row) => formatDateTime(row.sentAt || row.createdAt, i18n.language)
    },
    {
      title: t("Clicks"),
      key: "clicks",
      width: 110,
      align: "right",
      render: (_, row) =>
        row.clicks ? (
          <span title={formatDateTime(row.lastClickedAt, i18n.language)}>
            {row.clicks} {row.platforms?.length ? `(${row.platforms.join(", ")})` : ""}
          </span>
        ) : (
          "—"
        )
    }
  ];

  const rows = (data?.data || []).map(r => ({ ...r, optedOut: Boolean(r.optedOutAt) }));
  const needsSetup = data?.isOwner && settings && !settings.platforms.length;

  return (
    <div className="notifications-page">
      <Row gutter={[16, 16]} className="nt-gap">
        {[
          [t("Requests sent"), stats.sent],
          [t("Clicked"), stats.clicked],
          [t("Click rate"), `${clickRate} %`],
          [t("Unsubscribed"), stats.optedOut]
        ].map(([title, value]) => (
          <Col xs={12} md={6} key={title}>
            <Card size="small">
              <Statistic title={title} value={value} />
            </Card>
          </Col>
        ))}
      </Row>

      {needsSetup && (
        <Alert
          className="nt-gap"
          type="info"
          showIcon
          icon={<StarOutlined />}
          message={t("Enter your Google Place ID to start collecting reviews.")}
          action={
            <Button size="small" onClick={() => setSettingsOpen(true)}>
              {t("Settings")}
            </Button>
          }
        />
      )}
      {settings && settings.platforms.length > 0 && !settings.enabled && (
        <Alert
          className="nt-gap"
          type="warning"
          showIcon
          message={t("Automatic review requests are off. You can still send them by hand.")}
        />
      )}

      <PageToolbar
        filters={
          <Segmented
            value={filter}
            onChange={value => {
              setFilter(value);
              setPage(1);
            }}
            options={[
              { value: "all", label: t("All") },
              { value: "sent", label: statuses.sent },
              { value: "clicked", label: t("Clicked") },
              { value: "skipped", label: statuses.skipped }
            ]}
          />
        }
        actions={
          <Space wrap>
            {data?.isOwner && (
              <Button icon={<SettingOutlined />} onClick={() => setSettingsOpen(true)}>
                {t("Settings")}
              </Button>
            )}
            <Button type="primary" icon={<SendOutlined />} onClick={() => setSendOpen(true)}>
              {t("Ask for a review")}
            </Button>
          </Space>
        }
      />

      <ResponsiveTable
        rowKey="_id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        locale={{ emptyText: t("No review requests yet.") }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: data?.count || 0,
          showSizeChanger: false,
          onChange: setPage
        }}
      />

      <SettingsModal
        open={settingsOpen}
        settings={settings}
        onClose={() => setSettingsOpen(false)}
        onSaved={value => setData(prev => (prev ? { ...prev, settings: value } : prev))}
      />
      <SendModal open={sendOpen} onClose={() => setSendOpen(false)} onSent={load} />
    </div>
  );
};

export default Reviews;
